import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { 
  Layers, 
  Plus, 
  Edit3, 
  Trash2, 
  Save,
  X,
  Calculator,
  AlertCircle,
  CheckCircle,
  Target,
  Settings,
  Archive,
  TreePine,
  Percent,
  Users,
  DollarSign
} from "lucide-react";
import { Alert, AlertDescription } from "./ui/alert";
import { Separator } from "./ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import axios from "axios";

const API = require('../services/apiBase').default || require('../services/apiBase');

const CustomPoolDesigner = ({ calculationId, businessUnits, onPoolsUpdate }) => {
  const [customPools, setCustomPools] = useState([]);
  const [templates, setTemplates] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [activeTab, setActiveTab] = useState("pools");
  
  // Form state for adding/editing pools
  const [showForm, setShowForm] = useState(false);
  const [editingPool, setEditingPool] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [formData, setFormData] = useState({
    pool_name: "",
    pool_code: "",
    pool_description: "",
    pool_category: "custom",
    allocation_method: "percentage",
    allocation_driver: "",
    total_pool_costs: "",
    parent_pool_id: "",
    nesting_level: 1,
    requires_100_percent: true,
    pool_template: "",
    allocation_targets: [
      { pool_id: "G&A", percentage: 50 },
      { pool_id: "OH", percentage: 50 }
    ]
  });

  // Allocation results state
  const [allocationResults, setAllocationResults] = useState([]);

  useEffect(() => {
    if (calculationId) {
      loadCustomPools();
      loadTemplates();
    }
  }, [calculationId]);

  const loadCustomPools = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(`${API}/custom-pools/${calculationId}`);
      const pools = response.data.custom_pools || [];
      setCustomPools(pools);
      
      if (onPoolsUpdate) {
        onPoolsUpdate(pools);
      }
    } catch (e) {
      // Not an error if no pools exist yet
      if (e.response?.status !== 404) {
        setError(e.response?.data?.detail || "Error loading custom pools");
      }
    } finally {
      setLoading(false);
    }
  };

  const loadTemplates = async () => {
    try {
      const response = await axios.get(`${API}/custom-pool-templates`);
      setTemplates(response.data.templates || {});
    } catch (e) {
      console.warn("Could not load pool templates:", e.message);
    }
  };

  const handleTemplateSelect = (templateKey) => {
    const template = templates[templateKey];
    if (template) {
      setFormData(prev => ({
        ...prev,
        pool_name: template.name,
        pool_description: template.description,
        allocation_method: template.allocation_method,
        pool_template: templateKey,
        allocation_targets: template.default_targets.map(target => ({ ...target }))
      }));
      setSelectedTemplate(templateKey);
    }
  };

  const handleSaveCustomPool = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    
    // Validation
    if (!formData.pool_name.trim() || !formData.pool_code.trim()) {
      setError("Pool name and code are required");
      setSaving(false);
      return;
    }

    // Check for duplicate pool codes
    const duplicateCode = customPools.find(pool => 
      pool.pool_code === formData.pool_code && pool.id !== editingPool?.id
    );
    if (duplicateCode) {
      setError("Pool code must be unique");
      setSaving(false);
      return;
    }

    // Validate allocation targets total 100% if required
    if (formData.requires_100_percent && formData.allocation_targets.length > 0) {
      const totalPercentage = formData.allocation_targets.reduce((sum, target) => 
        sum + (parseFloat(target.percentage) || 0), 0
      );
      if (Math.abs(totalPercentage - 100) > 0.01) {
        setError(`Allocation percentages must total 100%, currently ${totalPercentage.toFixed(2)}%`);
        setSaving(false);
        return;
      }
    }

    try {
      const poolData = {
        calculation_id: calculationId,
        pool_name: formData.pool_name.trim(),
        pool_code: formData.pool_code.trim().toUpperCase(),
        pool_description: formData.pool_description.trim() || null,
        pool_category: formData.pool_category,
        allocation_method: formData.allocation_method,
        allocation_driver: formData.allocation_driver || null,
        total_pool_costs: parseFloat(formData.total_pool_costs) || 0,
        parent_pool_id: formData.parent_pool_id || null,
        nesting_level: parseInt(formData.nesting_level) || 1,
        requires_100_percent: formData.requires_100_percent,
        pool_template: formData.pool_template || null,
        allocation_targets: formData.allocation_targets.map(target => ({
          pool_id: target.pool_id,
          percentage: parseFloat(target.percentage) || 0
        })),
        created_by: "user"
      };

      let response;
      if (editingPool) {
        response = await axios.put(`${API}/custom-pools/${editingPool.id}`, poolData);
      } else {
        response = await axios.post(`${API}/custom-pools`, poolData);
      }

      if (response.data.success) {
        setSuccess(editingPool ? "Custom pool updated successfully!" : "Custom pool created successfully!");
        resetForm();
        loadCustomPools(); // Reload the list
      }
    } catch (e) {
      setError(e.response?.data?.detail || "Error saving custom pool");
    } finally {
      setSaving(false);
    }
  };

  const handleEditPool = (pool) => {
    setEditingPool(pool);
    setFormData({
      pool_name: pool.pool_name || "",
      pool_code: pool.pool_code || "",
      pool_description: pool.pool_description || "",
      pool_category: pool.pool_category || "custom",
      allocation_method: pool.allocation_method || "percentage",
      allocation_driver: pool.allocation_driver || "",
      total_pool_costs: pool.total_pool_costs ? pool.total_pool_costs.toString() : "",
      parent_pool_id: pool.parent_pool_id || "",
      nesting_level: pool.nesting_level || 1,
      requires_100_percent: pool.requires_100_percent !== false,
      pool_template: pool.pool_template || "",
      allocation_targets: pool.allocation_targets || []
    });
    setShowForm(true);
  };

  const handleDeletePool = async (poolId) => {
    if (!window.confirm("Are you sure you want to delete this custom pool?")) {
      return;
    }

    try {
      const response = await axios.delete(`${API}/custom-pools/${poolId}`);
      if (response.data.success) {
        setSuccess("Custom pool deleted successfully!");
        loadCustomPools();
      }
    } catch (e) {
      setError(e.response?.data?.detail || "Error deleting custom pool");
    }
  };

  const handleCalculateAllocations = async () => {
    if (!calculationId) {
      setError("No calculation ID available for allocation calculation");
      return;
    }

    setCalculating(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await axios.post(`${API}/calculate-custom-pool-allocation/${calculationId}`);

      if (response.data.success) {
        setSuccess("Custom pool allocation calculated successfully!");
        setAllocationResults(response.data.allocation_results || []);
      }
    } catch (e) {
      setError(e.response?.data?.detail || "Error calculating custom pool allocations");
    } finally {
      setCalculating(false);
    }
  };

  const resetForm = () => {
    setFormData({
      pool_name: "",
      pool_code: "",
      pool_description: "",
      pool_category: "custom",
      allocation_method: "percentage",
      allocation_driver: "",
      total_pool_costs: "",
      parent_pool_id: "",
      nesting_level: 1,
      requires_100_percent: true,
      pool_template: "",
      allocation_targets: [
        { pool_id: "G&A", percentage: 50 },
        { pool_id: "OH", percentage: 50 }
      ]
    });
    setEditingPool(null);
    setSelectedTemplate("");
    setShowForm(false);
  };

  const addAllocationTarget = () => {
    setFormData(prev => ({
      ...prev,
      allocation_targets: [
        ...prev.allocation_targets,
        { pool_id: "G&A", percentage: 0 }
      ]
    }));
  };

  const removeAllocationTarget = (index) => {
    setFormData(prev => ({
      ...prev,
      allocation_targets: prev.allocation_targets.filter((_, i) => i !== index)
    }));
  };

  const updateAllocationTarget = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      allocation_targets: prev.allocation_targets.map((target, i) => 
        i === index ? { ...target, [field]: value } : target
      )
    }));
  };

  const formatCurrency = (value) => {
    return value ? `$${value.toLocaleString()}` : '$0';
  };

  const getPoolsByNestingLevel = () => {
    const levels = {};
    customPools.forEach(pool => {
      const level = pool.nesting_level || 1;
      if (!levels[level]) levels[level] = [];
      levels[level].push(pool);
    });
    return levels;
  };

  const calculateTotalPercentage = () => {
    return formData.allocation_targets.reduce((sum, target) => 
      sum + (parseFloat(target.percentage) || 0), 0
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2">Loading custom pools...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Success/Error Messages */}
      {error && (
        <Alert className="border-red-200 bg-red-50">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="text-red-700">
            {error}
          </AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert className="border-green-200 bg-green-50">
          <CheckCircle className="h-4 w-4" />
          <AlertDescription className="text-green-700">
            {success}
          </AlertDescription>
        </Alert>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="pools" className="flex items-center space-x-2">
            <Layers className="h-4 w-4" />
            <span>Custom Pools</span>
          </TabsTrigger>
          <TabsTrigger value="templates" className="flex items-center space-x-2">
            <Archive className="h-4 w-4" />
            <span>Templates</span>
          </TabsTrigger>
          <TabsTrigger value="results" className="flex items-center space-x-2">
            <Calculator className="h-4 w-4" />
            <span>Results</span>
          </TabsTrigger>
        </TabsList>

        {/* Custom Pools Tab */}
        <TabsContent value="pools" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center space-x-2">
                  <Layers className="h-5 w-5 text-indigo-600" />
                  <span>Custom Pools</span>
                </CardTitle>
                <div className="flex items-center space-x-2">
                  <Button 
                    onClick={handleCalculateAllocations}
                    disabled={calculating || customPools.length === 0}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <Calculator className="h-4 w-4 mr-2" />
                    {calculating ? "Calculating..." : "Calculate Allocations"}
                  </Button>
                  <Button 
                    onClick={() => setShowForm(true)}
                    className="bg-indigo-600 hover:bg-indigo-700"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Custom Pool
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {customPools.length === 0 ? (
                <div className="text-center py-8">
                  <Layers className="h-16 w-16 mx-auto mb-4 text-slate-300" />
                  <h3 className="text-lg font-medium mb-2 text-slate-600">No Custom Pools</h3>
                  <p className="text-sm text-slate-500 mb-4">
                    Create custom pools to define specialized cost allocation beyond standard G&A, Overhead, and Fringe pools.
                  </p>
                  <Button 
                    onClick={() => setShowForm(true)}
                    className="bg-indigo-600 hover:bg-indigo-700"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Create First Custom Pool
                  </Button>
                </div>
              ) : (
                <div className="space-y-6">
                  {Object.entries(getPoolsByNestingLevel()).map(([level, pools]) => (
                    <div key={level}>
                      <h4 className="flex items-center space-x-2 font-semibold text-slate-700 mb-3">
                        <TreePine className="h-4 w-4" />
                        <span>Level {level} Pools</span>
                        {level > 1 && <Badge variant="outline">Nested</Badge>}
                      </h4>
                      <div className="rounded-md border">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Pool Name</TableHead>
                              <TableHead>Code</TableHead>
                              <TableHead>Category</TableHead>
                              <TableHead>Allocation Method</TableHead>
                              <TableHead className="text-right">Pool Costs</TableHead>
                              <TableHead>Targets</TableHead>
                              <TableHead>Actions</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {pools.map((pool) => (
                              <TableRow key={pool.id}>
                                <TableCell className="font-medium">
                                  <div className="flex items-center space-x-2">
                                    {pool.parent_pool_id && (
                                      <div className="w-4 h-4 border-l-2 border-b-2 border-slate-300 ml-2"></div>
                                    )}
                                    <span>{pool.pool_name}</span>
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <Badge variant="outline">{pool.pool_code}</Badge>
                                </TableCell>
                                <TableCell>
                                  <Badge variant="secondary">{pool.pool_category}</Badge>
                                </TableCell>
                                <TableCell>
                                  <div className="flex items-center space-x-1">
                                    {pool.allocation_method === "percentage" && <Percent className="h-3 w-3" />}
                                    {pool.allocation_method === "headcount" && <Users className="h-3 w-3" />}
                                    {pool.allocation_method === "revenue" && <DollarSign className="h-3 w-3" />}
                                    <span className="capitalize">{pool.allocation_method}</span>
                                  </div>
                                </TableCell>
                                <TableCell className="text-right">
                                  {formatCurrency(pool.total_pool_costs)}
                                </TableCell>
                                <TableCell>
                                  <div className="flex flex-wrap gap-1">
                                    {(pool.allocation_targets || []).map((target, idx) => (
                                      <Badge key={idx} variant="outline" className="text-xs">
                                        {target.pool_id}: {target.percentage}%
                                      </Badge>
                                    ))}
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <div className="flex items-center space-x-1">
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => handleEditPool(pool)}
                                    >
                                      <Edit3 className="h-3 w-3 mr-1" />
                                      Edit
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="text-red-600 hover:bg-red-50"
                                      onClick={() => handleDeletePool(pool.id)}
                                    >
                                      <Trash2 className="h-3 w-3" />
                                    </Button>
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Templates Tab */}
        <TabsContent value="templates" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Archive className="h-5 w-5 text-blue-600" />
                <span>Pool Templates</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {Object.entries(templates).map(([key, template]) => (
                  <Card key={key} className="cursor-pointer hover:bg-slate-50 transition-colors">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-semibold flex items-center justify-between">
                        <span>{template.name}</span>
                        <Badge variant="outline" className="text-xs">{key}</Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <p className="text-xs text-slate-600 mb-3">{template.description}</p>
                      <div className="text-xs space-y-1">
                        <div className="flex justify-between">
                          <span className="font-medium">Method:</span>
                          <span className="capitalize">{template.allocation_method}</span>
                        </div>
                        <div className="text-xs">
                          <span className="font-medium">Default Targets:</span>
                          <div className="mt-1 flex flex-wrap gap-1">
                            {template.default_targets?.map((target, idx) => (
                              <Badge key={idx} variant="outline" className="text-xs">
                                {target.pool_id}: {target.percentage}%
                              </Badge>
                            ))}
                          </div>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        className="w-full mt-3"
                        onClick={() => {
                          handleTemplateSelect(key);
                          setShowForm(true);
                        }}
                      >
                        <Target className="h-3 w-3 mr-1" />
                        Use Template
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Results Tab */}
        <TabsContent value="results" className="space-y-6">
          {allocationResults.length > 0 ? (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Calculator className="h-5 w-5 text-green-600" />
                  <span>Custom Pool Allocation Results</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {allocationResults.map((result, index) => (
                  <Card key={index} className="mb-4">
                    <CardHeader>
                      <CardTitle className="text-base flex items-center justify-between">
                        <span>{result.custom_pool_name}</span>
                        <Badge variant="outline">{result.allocation_method}</Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <h5 className="font-semibold mb-2">Allocated Amounts</h5>
                          {Object.entries(result.allocated_amounts || {}).map(([pool, amount]) => (
                            <div key={pool} className="flex justify-between text-sm">
                              <span>{pool}:</span>
                              <span className="font-medium">{formatCurrency(amount)}</span>
                            </div>
                          ))}
                        </div>
                        <div>
                          <h5 className="font-semibold mb-2">Allocation Percentages</h5>
                          {Object.entries(result.allocation_percentages || {}).map(([pool, percentage]) => (
                            <div key={pool} className="flex justify-between text-sm">
                              <span>{pool}:</span>
                              <span className="font-medium">{percentage.toFixed(2)}%</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      <Separator className="my-4" />
                      <div className="text-sm text-slate-600">
                        <div className="flex justify-between">
                          <span>Source Pool Costs:</span>
                          <span className="font-medium">{formatCurrency(result.source_pool_costs)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Target Pools:</span>
                          <span>{result.target_pools?.join(", ")}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="text-center py-8">
                <Calculator className="h-16 w-16 mx-auto mb-4 text-slate-300" />
                <h3 className="text-lg font-medium mb-2 text-slate-600">No Allocation Results</h3>
                <p className="text-sm text-slate-500 mb-4">
                  Configure custom pools and calculate allocations to see results here.
                </p>
                <Button
                  onClick={() => setActiveTab("pools")}
                  className="bg-indigo-600 hover:bg-indigo-700"
                >
                  Configure Custom Pools
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Add/Edit Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4 flex items-center space-x-2">
              <Layers className="h-5 w-5" />
              <span>{editingPool ? "Edit Custom Pool" : "Add Custom Pool"}</span>
              {selectedTemplate && (
                <Badge variant="outline">Using {selectedTemplate} template</Badge>
              )}
            </h3>
            
            <div className="space-y-4">
              {/* Basic Information */}
              <div>
                <h4 className="font-semibold mb-3 text-slate-700">Pool Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="pool_name">Pool Name *</Label>
                    <Input
                      id="pool_name"
                      placeholder="e.g., IT Services Pool"
                      value={formData.pool_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, pool_name: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label htmlFor="pool_code">Pool Code *</Label>
                    <Input
                      id="pool_code"
                      placeholder="e.g., IT"
                      value={formData.pool_code}
                      onChange={(e) => setFormData(prev => ({ ...prev, pool_code: e.target.value.toUpperCase() }))}
                    />
                  </div>
                </div>
                
                <div className="mt-4">
                  <Label htmlFor="pool_description">Description</Label>
                  <Textarea
                    id="pool_description"
                    placeholder="Describe this custom pool's purpose and allocation logic..."
                    value={formData.pool_description}
                    onChange={(e) => setFormData(prev => ({ ...prev, pool_description: e.target.value }))}
                    rows={3}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                  <div>
                    <Label htmlFor="pool_category">Category</Label>
                    <Select
                      value={formData.pool_category}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, pool_category: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="custom">Custom</SelectItem>
                        <SelectItem value="facilities">Facilities</SelectItem>
                        <SelectItem value="it">IT Services</SelectItem>
                        <SelectItem value="hr">Human Resources</SelectItem>
                        <SelectItem value="engineering">Engineering</SelectItem>
                        <SelectItem value="cleared">Cleared Labor</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="total_pool_costs">Total Pool Costs ($)</Label>
                    <Input
                      id="total_pool_costs"
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="100000.00"
                      value={formData.total_pool_costs}
                      onChange={(e) => setFormData(prev => ({ ...prev, total_pool_costs: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label htmlFor="nesting_level">Nesting Level</Label>
                    <Select
                      value={formData.nesting_level.toString()}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, nesting_level: parseInt(value) }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">Level 1 (Top)</SelectItem>
                        <SelectItem value="2">Level 2 (Nested)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <Separator />

              {/* Allocation Settings */}
              <div>
                <h4 className="font-semibold mb-3 text-slate-700">Allocation Settings</h4>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <Label htmlFor="allocation_method">Allocation Method</Label>
                    <Select
                      value={formData.allocation_method}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, allocation_method: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="percentage">Percentage</SelectItem>
                        <SelectItem value="headcount">Headcount</SelectItem>
                        <SelectItem value="payroll">Payroll</SelectItem>
                        <SelectItem value="revenue">Revenue</SelectItem>
                        <SelectItem value="square_footage">Square Footage</SelectItem>
                        <SelectItem value="driver_based">Driver Based</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {formData.allocation_method === "driver_based" && (
                    <div>
                      <Label htmlFor="allocation_driver">Allocation Driver</Label>
                      <Input
                        id="allocation_driver"
                        placeholder="e.g., engineering_headcount"
                        value={formData.allocation_driver}
                        onChange={(e) => setFormData(prev => ({ ...prev, allocation_driver: e.target.value }))}
                      />
                    </div>
                  )}
                </div>

                <div className="flex items-center space-x-2 mb-4">
                  <input
                    id="requires_100_percent"
                    type="checkbox"
                    checked={formData.requires_100_percent}
                    onChange={(e) => setFormData(prev => ({ ...prev, requires_100_percent: e.target.checked }))}
                  />
                  <Label htmlFor="requires_100_percent" className="text-sm">
                    Require allocation percentages to total 100%
                  </Label>
                </div>

                {/* Allocation Targets */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <Label>Allocation Targets</Label>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={addAllocationTarget}
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      Add Target
                    </Button>
                  </div>
                  
                  <div className="space-y-2 mb-3">
                    {formData.allocation_targets.map((target, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <Select
                          value={target.pool_id}
                          onValueChange={(value) => updateAllocationTarget(index, 'pool_id', value)}
                        >
                          <SelectTrigger className="flex-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="G&A">G&A</SelectItem>
                            <SelectItem value="OH">Overhead</SelectItem>
                            <SelectItem value="Fringe">Fringe</SelectItem>
                            {customPools.map(pool => (
                              <SelectItem key={pool.id} value={pool.id}>
                                {pool.pool_name} ({pool.pool_code})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          max="100"
                          placeholder="Percentage"
                          value={target.percentage}
                          onChange={(e) => updateAllocationTarget(index, 'percentage', e.target.value)}
                          className="w-24"
                        />
                        <span className="text-sm text-slate-500">%</span>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => removeAllocationTarget(index)}
                          disabled={formData.allocation_targets.length === 1}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                  
                  {formData.requires_100_percent && (
                    <div className="text-sm">
                      <span className="text-slate-600">Total Percentage: </span>
                      <span className={`font-medium ${
                        Math.abs(calculateTotalPercentage() - 100) < 0.01 
                          ? 'text-green-600' 
                          : 'text-red-600'
                      }`}>
                        {calculateTotalPercentage().toFixed(2)}%
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="flex justify-end space-x-2 mt-6">
              <Button 
                variant="outline" 
                onClick={() => {
                  resetForm();
                  setError(null);
                  setSuccess(null);
                }}
                disabled={saving}
              >
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
              <Button 
                onClick={handleSaveCustomPool}
                disabled={saving || !formData.pool_name.trim() || !formData.pool_code.trim()}
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                <Save className="h-4 w-4 mr-2" />
                {saving ? "Saving..." : (editingPool ? "Update Pool" : "Create Pool")}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Information Panel */}
      <div className="p-4 bg-slate-50 rounded-lg">
        <div className="flex items-center space-x-2 text-sm text-slate-600">
          <AlertCircle className="h-4 w-4" />
          <div>
            <p className="font-medium">Custom Pool Designer Guidelines</p>
            <ul className="text-xs mt-1 space-y-1">
              <li>• Custom pools enable specialized cost allocations beyond standard G&A, Overhead, and Fringe</li>
              <li>• Use templates for common scenarios (Facilities, IT, HR, Engineering, Cleared Labor)</li>
              <li>• Nested pools (Level 2) create parent-child relationships for complex allocation flows</li>
              <li>• Pool codes must be unique and are used for allocation tracking and reporting</li>
              <li>• Percentage-based allocations typically require targets to total 100%</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomPoolDesigner;